import React, { useMemo, useState } from 'react';
import { Box, Button, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { INVOICE_STATUS } from '../../utils/invoiceSchema';
import { iv, ivKpiCardSx, ivSectionTitleSx, ivSurfaceSx } from './invoiceTheme';

const formatMoney = (v) =>
  Number(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PERIOD = {
  D7: '7d',
  D15: '15d',
  D30: '30d',
  ALL: 'all'
};

const PERIOD_OPTIONS = [
  { value: PERIOD.D7, label: '7 Gün', days: 7 },
  { value: PERIOD.D15, label: '15 Gün', days: 15 },
  { value: PERIOD.D30, label: '30 Gün', days: 30 },
  { value: PERIOD.ALL, label: 'Tümü', days: null }
];

const resolveMs = (row) => {
  const ts =
    row?.sentAt?.toDate?.()?.getTime?.() ||
    row?.respondedAt?.toDate?.()?.getTime?.() ||
    row?.syncedAt?.toDate?.()?.getTime?.() ||
    row?.updatedAt?.toDate?.()?.getTime?.() ||
    row?.createdAt?.toDate?.()?.getTime?.() ||
    0;
  if (ts) return ts;
  if (row?.issueDate) {
    const d = Date.parse(row.issueDate);
    return Number.isFinite(d) ? d : 0;
  }
  return 0;
};

/**
 * Tarihi çözülemeyen kayıtlar dönem dışıdır.
 * Önceden bunlar her döneme dahil ediliyor ve "7 Gün" gibi dar seçimlerde
 * eski/bozuk kayıtlar tüm KPI'ları eşit şekilde şişiriyordu.
 */
const inPeriod = (row, days) => {
  if (days == null) return true;
  const ms = resolveMs(row);
  if (!ms) return false;
  return ms >= Date.now() - days * 24 * 60 * 60 * 1000;
};

/** Dönem filtresine girmeyen, tarihi okunamayan kayıt sayısı (kullanıcıya bildirilir). */
const countUndated = (rows) => (rows || []).filter((row) => !resolveMs(row)).length;

const Kpi = ({ title, value, subtitle, accent }) => (
  <Box sx={ivKpiCardSx(accent)}>
    <Typography sx={{ ...ivSectionTitleSx, mb: 0.8 }}>{title}</Typography>
    <Typography sx={{ fontSize: 28, fontWeight: 800, color: iv.text, lineHeight: 1.1 }}>{value}</Typography>
    {subtitle ? (
      <Typography sx={{ mt: 0.6, fontSize: 12.5, color: iv.textMuted, fontWeight: 600 }}>{subtitle}</Typography>
    ) : null}
  </Box>
);

function InvoiceReportsPanel({
  matches = [],
  inboundRows = [],
  onGoOutbound,
  onGoInbound,
  onGoApproval,
  /**
   * Onay bekleyen taslak sayısı. Kuralı (eksik bilgi var mı, onaya hazır mı)
   * Faturalar ekranı hesaplıyor; burada yeniden hesaplamak yerine ondan
   * alınıyor ki iki ekran farklı sayı göstermesin.
   */
  pendingApprovalCount = null
}) {
  const [period, setPeriod] = useState(PERIOD.D15);
  const days = PERIOD_OPTIONS.find((p) => p.value === period)?.days ?? 15;

  const stats = useMemo(() => {
    const outbound = (matches || []).filter((m) =>
      [INVOICE_STATUS.SENT, INVOICE_STATUS.APPROVED, INVOICE_STATUS.ERROR].includes(m.invoiceStatus)
    );
    const outboundScoped = outbound.filter((m) => inPeriod(m, days));
    const inboundScoped = (inboundRows || []).filter((m) => inPeriod(m, days));

    const sent = outboundScoped.filter((m) =>
      [INVOICE_STATUS.SENT, INVOICE_STATUS.APPROVED].includes(m.invoiceStatus)
    );
    const approved = outboundScoped.filter((m) => m.invoiceStatus === INVOICE_STATUS.APPROVED);
    const errors = outboundScoped.filter((m) => m.invoiceStatus === INVOICE_STATUS.ERROR);
    const inboundPending = inboundScoped.filter((m) => (m.inboundStatus || 'pending') === 'pending');
    const inboundAccepted = inboundScoped.filter((m) => m.inboundStatus === 'accepted');
    const inboundRejected = inboundScoped.filter((m) => m.inboundStatus === 'rejected');

    // invoiceTotals'ta "genelToplam" diye bir alan yok; doğru alan payableAmount.
    // Eski hâlinde tutar hep banka hareketine düşüyor, sıfırdan kesilen faturalarda 0 kalıyordu.
    const outboundAmount = sent.reduce((sum, m) => {
      const total =
        Number(m?.invoiceDraft?.invoiceTotals?.payableAmount) ||
        Number(m?.invoiceDraft?.amount) ||
        Number(m?.bankMovementData?.amount) ||
        0;
      return sum + total;
    }, 0);
    const inboundAmount = inboundScoped.reduce((sum, m) => sum + (Number(m.payableAmount) || 0), 0);

    const draftsPending = (matches || []).filter((m) => m.invoiceStatus === INVOICE_STATUS.READY).length;

    return {
      sentCount: sent.length,
      approvedCount: approved.length,
      errorCount: errors.length,
      outboundAmount,
      inboundCount: inboundScoped.length,
      inboundPending: inboundPending.length,
      inboundAccepted: inboundAccepted.length,
      inboundRejected: inboundRejected.length,
      inboundAmount,
      draftsPending,
      undatedCount: days == null ? 0 : countUndated(outbound) + countUndated(inboundRows)
    };
  }, [matches, inboundRows, days]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0, overflow: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: iv.text }}>Raporlar</Typography>
          <Typography sx={{ fontSize: 13, color: iv.textMuted, mt: 0.3 }}>
            Giden / gelen e-Fatura özeti (minimal)
          </Typography>
          {stats.undatedCount > 0 ? (
            <Typography sx={{ fontSize: 12, color: iv.warning, mt: 0.3, fontWeight: 700 }}>
              {stats.undatedCount} kaydın tarihi okunamadı; dönem toplamlarına girmiyor.
            </Typography>
          ) : null}
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={period}
          onChange={(_, v) => v && setPeriod(v)}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, fontSize: 12 } }}
        >
          {PERIOD_OPTIONS.map((p) => (
            <ToggleButton key={p.value} value={p.value}>
              {p.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box
        sx={{
          display: 'grid',
          /* Telefonda tek sutunda her kartin sag yarisi bostu */
          gridTemplateColumns: { xs: '1fr 1fr', lg: 'repeat(4, 1fr)' },
          gap: 1.25
        }}
      >
        <Kpi
          title="Giden (gönderilen)"
          value={stats.sentCount}
          subtitle={`${formatMoney(stats.outboundAmount)} ₺ · ${stats.approvedCount} GİB onaylı`}
          accent={iv.success}
        />
        <Kpi title="Giden hata" value={stats.errorCount} subtitle="Yeniden gönderim gerekebilir" accent={iv.danger} />
        <Kpi
          title="Gelen"
          value={stats.inboundCount}
          subtitle={`${formatMoney(stats.inboundAmount)} ₺ · ${stats.inboundPending} bekliyor`}
          accent={iv.primary}
        />
        <Kpi
          title="Gelen yanıt"
          value={`${stats.inboundAccepted} / ${stats.inboundRejected}`}
          subtitle="Kabul / Red"
          accent={iv.warning}
        />
      </Box>

      <Box sx={ivSurfaceSx}>
        <Typography sx={{ ...ivSectionTitleSx, px: 1.75, pt: 1.5, pb: 1 }}>Hızlı erişim</Typography>
        <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1} sx={{ px: 1.75, pb: 1.75 }}>
          <Button
            size="small"
            onClick={onGoOutbound}
            sx={{ textTransform: 'none', fontWeight: 700, border: `1px solid ${iv.border}`, bgcolor: '#fff' }}
          >
            Giden Belgeler
          </Button>
          <Button
            size="small"
            onClick={onGoInbound}
            sx={{ textTransform: 'none', fontWeight: 700, border: `1px solid ${iv.border}`, bgcolor: '#fff' }}
          >
            Gelen Belgeler
          </Button>
          <Button
            size="small"
            onClick={onGoApproval}
            sx={{ textTransform: 'none', fontWeight: 700, border: `1px solid ${iv.border}`, bgcolor: '#fff' }}
          >
            Onay Bekleyenler ({pendingApprovalCount ?? stats.draftsPending})
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}

export default InvoiceReportsPanel;
